// lib/auto-cut.ts
import type { CV, Mat } from '@techstark/opencv-js';
import { projectStudioLayerWorldRectToLocal, reprojectStudioLayerWorldImageToLocal, type StudioLayerTransform } from '#lib/studio-layer-transform';
import type { Rect } from '#types/types';

/** Auto Cut keeps its analysis render as the layer: past this pixel count, a layer loses density. */
export const MAX_AUTO_CUT_ANALYSIS_PIXELS = 4096 * 4096;

interface DiffBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  hasContent: boolean;
}

function computeAlphaBounds(imageData: ImageData): DiffBounds {
  const { width, height, data } = imageData;

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha !== undefined && alpha > 0) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  const hasContent = maxX >= 0 && maxY >= 0;

  if (!hasContent) {
    return {
      minX: 0,
      minY: 0,
      maxX: -1,
      maxY: -1,
      hasContent: false,
    };
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    hasContent: true,
  };
}

/**
 * Where `candidate` differs from `base`: grey difference, blurred, thresholded, closed, dilated by a margin, holes
 * filled and feathered. A CV_8U mask the caller deletes; Extract keeps the pixels under it.
 */
export const computeChangeMask = (cv: any, baseMat: any, candidateMat: any): any => {
  let baseGray: any;
  let candidateGray: any;
  let diffGray: any;
  let diffBlurred: any;
  let changeMask: any;
  let morphKernel: any;
  let dilationKernel: any;
  let unchanged: any;
  let floodMask: any;
  let holes: any;
  let featheredMask: any;
  try {
    baseGray = new cv.Mat();
    candidateGray = new cv.Mat();
    cv.cvtColor(baseMat, baseGray, cv.COLOR_RGBA2GRAY);
    cv.cvtColor(candidateMat, candidateGray, cv.COLOR_RGBA2GRAY);

    diffGray = new cv.Mat();
    cv.absdiff(baseGray, candidateGray, diffGray);

    const blurKernelSize = 31;
    diffBlurred = new cv.Mat();
    cv.GaussianBlur(diffGray, diffBlurred, new cv.Size(blurKernelSize, blurKernelSize), 0);

    const thresholdValue = 8;
    changeMask = new cv.Mat();
    cv.threshold(diffBlurred, changeMask, thresholdValue, 255, cv.THRESH_BINARY);

    const morphKernelSize = 9;
    morphKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(morphKernelSize, morphKernelSize));
    cv.morphologyEx(changeMask, changeMask, cv.MORPH_CLOSE, morphKernel, new cv.Point(-1, -1), 3);

    const marginSize = 11;
    dilationKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(marginSize, marginSize));
    cv.dilate(changeMask, changeMask, dilationKernel);

    // An unchanged island enclosed by change is new content that happens to match the old grey level: it is
    // changed. The 1 px unchanged frame lets one corner seed reach every border pixel.
    unchanged = new cv.Mat();
    cv.bitwise_not(changeMask, unchanged);
    cv.copyMakeBorder(unchanged, unchanged, 1, 1, 1, 1, cv.BORDER_CONSTANT, new cv.Scalar(255));
    floodMask = cv.Mat.zeros(unchanged.rows + 2, unchanged.cols + 2, cv.CV_8U);
    cv.floodFill(unchanged, floodMask, new cv.Point(0, 0), new cv.Scalar(0));
    holes = unchanged.roi(new cv.Rect(1, 1, changeMask.cols, changeMask.rows));
    cv.bitwise_or(changeMask, holes, changeMask);

    const featherAmount = 11;
    featheredMask = new cv.Mat();
    cv.GaussianBlur(changeMask, featheredMask, new cv.Size(featherAmount, featherAmount), 0);
    const result = featheredMask;
    featheredMask = null;
    return result;
  } finally {
    dilationKernel?.delete();
    holes?.delete();
    floodMask?.delete();
    unchanged?.delete();
    featheredMask?.delete();
    baseGray?.delete();
    candidateGray?.delete();
    diffGray?.delete();
    diffBlurred?.delete();
    changeMask?.delete();
    morphKernel?.delete();
  }
};

const cropToBounds = (image: ImageData, { minX, minY, maxX, maxY }: DiffBounds): ImageData => {
  if (minX === 0 && minY === 0 && maxX === image.width - 1 && maxY === image.height - 1) return image;
  const cropped = new ImageData(maxX - minX + 1, maxY - minY + 1);
  const rowLength = cropped.width * 4;
  for (let y = 0; y < cropped.height; y++) {
    const from = ((minY + y) * image.width + minX) * 4;
    cropped.data.set(image.data.subarray(from, from + rowLength), y * rowLength);
  }
  return cropped;
};

export type AutoCutGeometry = {
  /** The world area both renders cover. */
  worldRect: Rect;
  layerWorldRect: Rect;
  transform: StudioLayerTransform;
  /** Render pixels per world unit. */
  analysisUpscaleFactor: number;
};

export type AutoCutExtraction = {
  /** Layer-local pixels at the renders' density; a transparent 1x1 when nothing changed. */
  imageData: ImageData;
  /** Where `imageData` sits in layer-local units; null when nothing changed. */
  localRect: Rect | null;
  hasContent: boolean;
};

/**
 * Auto Cut: the pixels of `modified` where it differs from `original` (same size, both rendered over
 * `geometry.worldRect`), reprojected into the layer's local space and cropped to their opaque bounds.
 * Null when the diff itself fails.
 */
export const computeAutoCutExtraction = (
  cv: CV,
  original: ImageData,
  modified: ImageData,
  geometry: AutoCutGeometry,
): AutoCutExtraction | null => {
  let originalMat: Mat | undefined;
  let modifiedMat: Mat | undefined;
  let changeMask: Mat | undefined;
  let keptMat: Mat | undefined;
  let diffImageData: ImageData;
  try {
    originalMat = cv.matFromImageData(original);
    modifiedMat = cv.matFromImageData(modified);
    if (originalMat.cols !== modifiedMat.cols || originalMat.rows !== modifiedMat.rows) {
      throw new Error('computeAutoCutExtraction: Images must share the same dimensions.');
    }
    changeMask = computeChangeMask(cv, originalMat, modifiedMat) as Mat;
    keptMat = cv.Mat.zeros(modifiedMat.rows, modifiedMat.cols, cv.CV_8UC4);
    modifiedMat.copyTo(keptMat, changeMask);
    diffImageData = new ImageData(new Uint8ClampedArray(keptMat.data), keptMat.cols, keptMat.rows);
  } catch (error) {
    console.error('computeAutoCutExtraction: Failed to compute image diff.', error);
    return null;
  } finally {
    originalMat?.delete();
    modifiedMat?.delete();
    changeMask?.delete();
    keptMat?.delete();
  }

  const bounds = computeAlphaBounds(diffImageData);
  if (!bounds.hasContent) return { imageData: new ImageData(1, 1), localRect: null, hasContent: false };

  const { worldRect, layerWorldRect, transform, analysisUpscaleFactor } = geometry;
  const diff = cropToBounds(diffImageData, bounds);
  const diffWorldRect = {
    x: worldRect.x + bounds.minX / analysisUpscaleFactor,
    y: worldRect.y + bounds.minY / analysisUpscaleFactor,
    w: diff.width / analysisUpscaleFactor,
    h: diff.height / analysisUpscaleFactor,
  };
  const projectedRect = projectStudioLayerWorldRectToLocal(diffWorldRect, layerWorldRect, transform);
  const local = reprojectStudioLayerWorldImageToLocal(
    diff,
    diffWorldRect,
    layerWorldRect,
    transform,
    projectedRect,
    Math.max(1, Math.ceil(projectedRect.w * analysisUpscaleFactor)),
    Math.max(1, Math.ceil(projectedRect.h * analysisUpscaleFactor)),
  );
  const localBounds = computeAlphaBounds(local);
  if (!localBounds.hasContent) return { imageData: local, localRect: projectedRect, hasContent: true };
  return {
    imageData: cropToBounds(local, localBounds),
    localRect: {
      x: projectedRect.x + localBounds.minX / analysisUpscaleFactor,
      y: projectedRect.y + localBounds.minY / analysisUpscaleFactor,
      w: (localBounds.maxX - localBounds.minX + 1) / analysisUpscaleFactor,
      h: (localBounds.maxY - localBounds.minY + 1) / analysisUpscaleFactor,
    },
    hasContent: true,
  };
};
